import { type GrapeMoney } from 'types'
import { type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansMetalTierItem as MetalTier, type MarketplaceProduct } from 'utils/web_api.schemas'

import { type MedicalPlanDisplayItem } from './medicalPlanTypes'
import {
  type AncillaryLineItem,
  type BenefitsBundleStrategy,
  type DentalPlan,
  type MarketplaceProductForBudget,
  type NonMedicalPlanFeature,
  type PlansForBenefitsBundle,
  type SmartBudgetPlanData,
  type VisionPlan,
} from './types'
import { extractInNetworkAmount, METAL_TIER_ORDER } from './utils'

export const ANCILLARY_FEATURE_SEPARATOR = ' • '

export const DENTAL_WAITING_PERIOD_ICON = '⚠️'

/**
 * Configuration for a benefits bundle strategy, including UI metadata and plan-finding logic
 */
interface StrategyConfig {
  key: BenefitsBundleStrategy
  label: string
  description: string
  findPlans: (
    plans: MedicalPlanDisplayItem[],
    allowance: GrapeMoney,
    minimumMetalTier: MetalTier
  ) => PlansForBenefitsBundle
}

/**
 * Unified configuration for all benefits bundle strategies.
 * Each strategy contains UI metadata (label, description) and the logic to find plans.
 */
export const STRATEGY_CONFIGS: Record<BenefitsBundleStrategy, Omit<StrategyConfig, 'key'>> = {
  flexible: {
    label: 'Flexible',
    description: 'Best if you want solid coverage and extra monthly funds to spend on other care',
    findPlans: (plans, allowance, minimumMetalTier) =>
      findMostFlexiblePlans(plans, allowance, minimumMetalTier),
  },
  balanced: {
    label: 'Balanced',
    description: 'Great for those seeking stronger coverage while keeping a small monthly cushion',
    findPlans: (plans, allowance, _minimumMetalTier) =>
      findBalancedPlans(plans, allowance),
  },
  traditional: {
    label: 'Traditional',
    description: "Ideal if you want the highest level of coverage and don't mind paying out of pocket for it",
    findPlans: (plans, _allowance, _minimumMetalTier) =>
      findMostTraditionalPlans(plans),
  },
} as const

/**
 * Ordered array of strategy keys for rendering (e.g., button order in UI)
 */
export const STRATEGY_ORDER: readonly BenefitsBundleStrategy[] = [
  'flexible',
  'balanced',
  'traditional',
] as const

/**
 * Sorts medical plans by lowest premium first, with higher actuarial value as tiebreaker
 */
const sortPlansByPremiumAndActuarialValue = (plans: MedicalPlanDisplayItem[]): MedicalPlanDisplayItem[] => {
  return plans.sort((a, b) => {
    const aPremium = a.total_premium?.subunits || 0
    const bPremium = b.total_premium?.subunits || 0
    // Primary sort: lowest premium first
    if (aPremium !== bPremium) {
      return aPremium - bPremium
    }
    // Tiebreaker: higher actuarial value first
    return b.plan.computed_actuarial_value - a.plan.computed_actuarial_value
  })
}

/**
 * Sorts medical plans by highest actuarial value first, with lower premium as tiebreaker
 */
const sortPlansByActuarialValueAndPremium = (plans: MedicalPlanDisplayItem[]): MedicalPlanDisplayItem[] => {
  return plans.sort((a, b) => {
    // Primary sort: highest actuarial value first
    const avDiff = b.plan.computed_actuarial_value - a.plan.computed_actuarial_value
    if (avDiff !== 0) {
      return avDiff
    }
    // Tiebreaker: lower premium first
    const aPremium = a.total_premium?.subunits || 0
    const bPremium = b.total_premium?.subunits || 0
    return aPremium - bPremium
  })
}

/**
 * Sorts medical plans by distance from allowance (closest first), with higher actuarial value as tiebreaker
 */
const sortPlansByDistanceFromAllowance = (
  plans: MedicalPlanDisplayItem[],
  allowanceSubunits: number
): MedicalPlanDisplayItem[] => {
  return plans.sort((a, b) => {
    const aPremium = a.total_premium?.subunits || 0
    const bPremium = b.total_premium?.subunits || 0

    const aDistance = Math.abs(aPremium - allowanceSubunits)
    const bDistance = Math.abs(bPremium - allowanceSubunits)

    // Primary sort: closest to allowance first
    if (aDistance !== bDistance) {
      return aDistance - bDistance
    }

    // Tiebreaker: higher actuarial value first
    return b.plan.computed_actuarial_value - a.plan.computed_actuarial_value
  })
}

/**
 * Filter function for finding balanced plans within 5% of member allowance.
 * Sorts by distance from allowance (absolute value), with higher AV as tiebreaker.
 * Fills array from closest to farthest, stopping when 10 plans accumulated or more than 5% away.
 * If allowance is 0 or null, treats it as $0 and returns plans closest to $0.
 */
const balancedPlansFilter = (
  plans: MedicalPlanDisplayItem[],
  allowance: GrapeMoney | null | undefined
): MedicalPlanDisplayItem[] => {
  if (plans.length === 0) {
    return []
  }

  const allowanceSubunits = allowance?.subunits || 0

  // Sort by distance from allowance (absolute value), with higher AV as tiebreaker
  const sortedPlans = sortPlansByDistanceFromAllowance([...plans], allowanceSubunits)

  const filteredPlans: MedicalPlanDisplayItem[] = []
  const fivePercentThreshold = allowanceSubunits * 0.05 // 5% of allowance

  for (const plan of sortedPlans) {
    const planPremium = plan.total_premium?.subunits || 0
    const distance = Math.abs(planPremium - allowanceSubunits)

    // If we have at least 1 plan, break if 10 plans accumulated or more than 5% away
    if (filteredPlans.length >= 1) {
      if (filteredPlans.length >= 10 || distance > fivePercentThreshold) {
        break
      }
    }

    filteredPlans.push(plan)
  }

  return filteredPlans
}

/**
 * Filter function for finding flexible plans at minimum metal tier or better.
 * Sorts by premium (lowest first), with higher AV as tiebreaker, then fills the array
 * from lowest to highest, stopping when over the allowance or when 10 plans are accumulated.
 */
const flexiblePlansFilter = (
  plans: MedicalPlanDisplayItem[],
  allowance: GrapeMoney | null | undefined,
  minimumMetalTier: MetalTier
): MedicalPlanDisplayItem[] => {
  // Filter to plans at the minimum metal tier or better
  const metalTierThreshold = METAL_TIER_ORDER[minimumMetalTier] ?? 999
  const plansAtMinimumTier = plans.filter(plan => {
    const planTierOrder = METAL_TIER_ORDER[plan.plan.metal_tier] ?? 999
    return planTierOrder <= metalTierThreshold
  })

  if (plansAtMinimumTier.length === 0) {
    return []
  }

  // Sort by premium (lowest first), with higher AV as tiebreaker
  const sortedPlans = sortPlansByPremiumAndActuarialValue([...plansAtMinimumTier])

  // Fill array from lowest to highest, stopping when over allowance or 10 plans accumulated
  const filteredPlans: MedicalPlanDisplayItem[] = []
  const allowanceSubunits = allowance?.subunits || 0

  for (const plan of sortedPlans) {
    const planPremium = plan.total_premium?.subunits || 0

    // If we have at least 1 plan, break if over allowance or if 10 plans accumulated
    if (filteredPlans.length >= 1) {
      if (planPremium > allowanceSubunits || filteredPlans.length >= 10) {
        break
      }
    }

    filteredPlans.push(plan)
  }

  return filteredPlans
}

/**
 * Filter function for finding traditional plans with highest actuarial values.
 * Sorts by AV (highest first), with lower premium as tiebreaker.
 * Fills the array from highest to lowest, stopping when 10 plans accumulated.
 */
const traditionalPlansFilter = (plans: MedicalPlanDisplayItem[]): MedicalPlanDisplayItem[] => {
  if (plans.length === 0) {
    return []
  }

  // Sort by AV (highest first), with lower premium as tiebreaker
  const sortedPlans = sortPlansByActuarialValueAndPremium([...plans])

  const filteredPlans: MedicalPlanDisplayItem[] = []

  // Fill array from highest to lowest, stopping when 10 plans accumulated
  for (const plan of sortedPlans) {
    // Break if 10 plans accumulated
    if (filteredPlans.length >= 10) {
      break
    }

    filteredPlans.push(plan)
  }

  return filteredPlans
}

/**
 * Generic helper function for finding plans with a specific filtering strategy
 */
const findPlansWithStrategy = (
  plans: MedicalPlanDisplayItem[],
  strategy: BenefitsBundleStrategy,
  filterFn: (plans: MedicalPlanDisplayItem[]) => MedicalPlanDisplayItem[]
): PlansForBenefitsBundle => {
  let recommendedPlan: MedicalPlanDisplayItem | undefined
  let allPlansForStrategy: MedicalPlanDisplayItem[] = []

  if (plans && plans.length > 0) {
    const filteredPlans = filterFn(plans)
    const sortedPlans = sortPlansByPremiumAndActuarialValue(filteredPlans)

    recommendedPlan = sortedPlans[0]
    allPlansForStrategy = sortedPlans
  }

  return {
    strategy,
    recommended_plan: recommendedPlan,
    all_plans_for_strategy: allPlansForStrategy,
  }
}

/**
 * Finds balanced plans by filtering plans within 5% of the member allowance,
 * sorted by distance from allowance (closest first). Recommends the plan with
 * the premium closest but under the member's allowance.
 *
 * "Balanced" means the plan that is close to the allowance
 *
 * @param plans Array of MedicalPlanDisplayItem
 * @param allowance The member's allowance amount as GrapeMoney object
 * @returns PlansForBenefitsBundle with the balanced strategy, or empty values if no plans qualify
 */
export const findBalancedPlans = (
  plans: MedicalPlanDisplayItem[],
  allowance: GrapeMoney | null | undefined
): PlansForBenefitsBundle => {
  let recommendedPlan: MedicalPlanDisplayItem | undefined
  let allPlansForStrategy: MedicalPlanDisplayItem[] = []

  if (plans && plans.length > 0) {
    const filteredPlans = balancedPlansFilter(plans, allowance)

    // Find the recommended plan: closest but under the member's allowance
    const allowanceSubunits = allowance?.subunits || 0

    // Start with the closest plan to allowance
    recommendedPlan = filteredPlans[0]

    // Loop through to find the first plan under the allowance
    for (const plan of filteredPlans) {
      const planPremium = plan.total_premium?.subunits || 0
      if (planPremium <= allowanceSubunits) {
        recommendedPlan = plan
        break
      }
    }

    // Re-sort by lowest premium then AV as tiebreaker
    allPlansForStrategy = sortPlansByPremiumAndActuarialValue([...filteredPlans])
  }

  return {
    strategy: 'balanced',
    recommended_plan: recommendedPlan,
    all_plans_for_strategy: allPlansForStrategy,
  }
}

/**
 * Finds the most flexible plans by filtering plans at the minimum metal tier or better,
 * sorting by premium (lowest first, with higher AV as tiebreaker), then filling the array
 * from lowest to highest, stopping when over the allowance or when 10 plans are accumulated.
 * Returns the plan with the lowest premium as the recommended plan.
 *
 * "Most flexible" means the plan has the lowest premium, which helps maximize the user's QME budget.
 *
 * @param plans Array of MedicalPlanDisplayItem
 * @param allowance The member's allowance amount as GrapeMoney object
 * @param minimumMetalTier Minimum metal tier to filter plans by
 * @returns PlansForBenefitsBundle with the most flexible strategy, or empty values if no plans qualify
 */
export const findMostFlexiblePlans = (
  plans: MedicalPlanDisplayItem[],
  allowance: GrapeMoney | null | undefined = null,
  minimumMetalTier: MetalTier = 'bronze'
): PlansForBenefitsBundle => {
  return findPlansWithStrategy(
    plans,
    'flexible',
    (plans) => flexiblePlansFilter(plans, allowance, minimumMetalTier)
  )
}

/**
 * Finds the most traditional plans by sorting by actuarial value (highest first) with lower premium
 * as tiebreaker, then taking up to 10 plans. Recommends the plan with the highest AV, irrespective of price.
 *
 * "Most traditional" means the plan has the highest actuarial value, prioritizing the greatest coverage
 * even if it eliminates the user's QME budget.
 *
 * @param plans Array of MedicalPlanDisplayItem
 * @returns PlansForBenefitsBundle with the most traditional strategy, or empty values if no plans qualify
 */
export const findMostTraditionalPlans = (
  plans: MedicalPlanDisplayItem[]
): PlansForBenefitsBundle => {
  let recommendedPlan: MedicalPlanDisplayItem | undefined
  let allPlansForStrategy: MedicalPlanDisplayItem[] = []

  if (plans && plans.length > 0) {
    const filteredPlans = traditionalPlansFilter(plans)

    // Recommend the plan with the highest AV (first plan in the sorted array)
    recommendedPlan = filteredPlans[0]

    // Re-sort by lowest premium with AV as tiebreaker for display
    allPlansForStrategy = sortPlansByPremiumAndActuarialValue(filteredPlans)
  }

  return {
    strategy: 'traditional',
    recommended_plan: recommendedPlan,
    all_plans_for_strategy: allPlansForStrategy,
  }
}

export const medicalPlanToPlanData = (medicalPlan: MedicalPlanDisplayItem, isIndividual: boolean): SmartBudgetPlanData => {
  const parsedDeductible = extractInNetworkAmount(
    isIndividual
      ? medicalPlan.plan.individual_medical_deductible
      : medicalPlan.plan.family_medical_deductible
  )

  const parsedMOOP = extractInNetworkAmount(
    isIndividual ? medicalPlan.plan.individual_medical_moop : medicalPlan.plan.family_medical_moop
  )

  const formattedDeductible = Number(parsedDeductible).toLocaleString('en-US')
  const formattedMOOP = Number(parsedMOOP).toLocaleString('en-US')

  return {
    carrier_icon_url: medicalPlan.plan.carrier_icon_url,
    type: 'medical',
    name: medicalPlan.plan.display_name,
    carrier: medicalPlan.plan.carrier_name,
    premium: {
      formatted_amount: medicalPlan.total_premium.formatted_amount,
      subunits: medicalPlan.total_premium.subunits,
      currency: medicalPlan.total_premium.currency as 'USD',
    },
    features: [`$${formattedDeductible} deductible`, `$${formattedMOOP} OOP max`] as [string, string],
  }
}

/**
 * Calculates the number of coverage months based on the soonest coverage start date.
 * If no soonest coverage start date is provided, returns 12.
 *
 * @param soonestCoverageStartDate Soonest coverage start date as a string
 * @returns Number of coverage months
 */
export const getNumberOfCoverageMonths = (soonestCoverageStartDate?: string): number => {
  if (!soonestCoverageStartDate) return 12
  const coverageStartDate = new Date(soonestCoverageStartDate)
  return 12 - coverageStartDate.getUTCMonth()
}

/**
 * Converts a MarketplaceProduct to a number of monthly subunits, based on the price cadence and the number of coverage months.
 * Yearly and one-time products are converted to monthly subunits by dividing the base price by the number of coverage months.
 *
 * @param product MarketplaceProduct
 * @param numberOfCoverageMonths Number of coverage months
 * @returns Number of monthly subunits
 */
export const getMonthlySubunits = (product: MarketplaceProduct, numberOfCoverageMonths: number): number => {
  if (numberOfCoverageMonths >= 2 && (product.price_cadence === 'one_time' || product.price_cadence === 'yearly')) {
    return Math.ceil(product.base_price.subunits / numberOfCoverageMonths)
  } else {
    return product.base_price.subunits
  }
}

/**
 * Picks the best products for a given budget, using a two-pointer approach to find the pair of products that is closest to the remaining balance.
 * If no pair is found, the single product that is closest to the remaining balance is returned.
 * If there is not enough budget to cover any product, an empty array is returned.
 *
 * @param sortedProducts Array of MarketplaceProductForBudget, sorted by monthlySubunits
 * @param remainingBalance The remaining balance as a number
 * @returns Array of MarketplaceProductForBudget
 */
export const pickMarketplaceProductsForBudget = (
  sortedProducts: MarketplaceProductForBudget[],
  remainingBalance: number
): MarketplaceProductForBudget[] => {
  if (!sortedProducts || sortedProducts.length === 0) return []
  if (sortedProducts.length === 1) return sortedProducts[0].monthlySubunits <= remainingBalance ? [sortedProducts[0]] : []

  let leftIndex = 0
  let rightIndex = sortedProducts.length - 1
  let bestProducts: MarketplaceProductForBudget[] | null = null
  let bestSum = 0

  // Find the pair of products that is closest to the remaining balance
  while (leftIndex < rightIndex) {
    const leftProduct = sortedProducts[leftIndex]
    const rightProduct = sortedProducts[rightIndex]
    const totalPrice = leftProduct.monthlySubunits + rightProduct.monthlySubunits

    if (totalPrice <= remainingBalance) {
      // This pair is within budget
      // Keep it if it's closer to the remaining balance than the current best
      if (totalPrice > bestSum) {
        bestSum = totalPrice
        bestProducts = [leftProduct, rightProduct]
      }
      // Move left index to the right to try a larger pair
      leftIndex++
    } else {
      // Move right index to the left to try a smaller pair
      rightIndex--
    }
  }

  if (bestProducts) return bestProducts

  // If no pair is found, try to return the single product that is closest to the remaining balance
  for (const product of sortedProducts) {
    if (product.monthlySubunits <= remainingBalance) {
      bestProducts = [product]
    } else {
      break
    }
  }

  return bestProducts || []
}

export const dentalPlanToPlanData = (dentalPlan: DentalPlan): SmartBudgetPlanData => {
  const features: [string, string] = ['', '']

  for (const feature of dentalPlan.features) {
    let found = false
    if (feature.header === 'Deductibles and limits') {
      for (const description of feature.descriptions) {
        if (description.includes('coverage')) {
          // TODO(@jason): This is brittle, in the future we should put these into the YAML file
          const coverageAndAmount = description.split(' • ')
          features[0] = coverageAndAmount[0]
          features[1] = coverageAndAmount[1]
          found = true
          break
        }
      }
    }
    if (found) break
  }

  return {
    carrier_icon_url: dentalPlan.carrier_icon_url,
    type: 'dental',
    name: dentalPlan.name,
    carrier: dentalPlan.carrier,
    premium: dentalPlan.premium,
    features,
  }
}

export const visionPlanToPlanData = (visionPlan: VisionPlan): SmartBudgetPlanData => {
  // TODO(@jason): This is extremely brittle, need to refactor in future

  let features: [string, string] = ['', '']

  if (visionPlan.carrier === 'BlueShield of California') {
    if (visionPlan.name === 'Ultimate Vision 15/25/120') {
      features = ['Premium coverage', '$120 for frames every 12 months']
    } else if (visionPlan.name === 'Ultimate Vision 15/25/150') {
      features = ['Premium coverage', '$150 for frames every 12 months']
    }
  } else if (visionPlan.carrier === 'VSP') {
    if (visionPlan.name === 'Standard') {
      features = ['$15 in-network eye exams', '$25 in-network lenses']
    } else if (visionPlan.name === 'EasyOptions') {
      features = ['All of VSP Standard', 'Choose your upgrade option']
    }
  }

  return {
    carrier_icon_url: visionPlan.carrier_icon_url,
    type: 'vision',
    name: visionPlan.name,
    carrier: visionPlan.carrier,
    premium: visionPlan.premium,
    features,
  }
}

export const cleanUpAncillaryString = (string: string): string => {
  return string.replace(DENTAL_WAITING_PERIOD_ICON, '').trim()
}

export const ancillaryDescriptionToLineItem = (description: string): AncillaryLineItem => {
  if (!description.includes(ANCILLARY_FEATURE_SEPARATOR)) {
    return {
      label: cleanUpAncillaryString(description),
      value: '',
    }
  } else {
    const labelAndValue = description.split(ANCILLARY_FEATURE_SEPARATOR)
    return {
      label: cleanUpAncillaryString(labelAndValue[0]),
      value: cleanUpAncillaryString(labelAndValue[1]),
    }
  }
}

export const makeDentalLineItem = (label: string, description: string): AncillaryLineItem => {
  if (!description.includes(ANCILLARY_FEATURE_SEPARATOR)) {
    return {
      label,
      value: cleanUpAncillaryString(description),
    }
  } else {
    const amounts = description.split(ANCILLARY_FEATURE_SEPARATOR)

    if (amounts[0].trim() === 'In and out-of-network') {
      return {
        label,
        value: cleanUpAncillaryString(amounts[1]),
      }
    } else if (!amounts[0].includes('network')) {
      const parts = description.toLowerCase().split(ANCILLARY_FEATURE_SEPARATOR)

      return {
        label,
        value: parts.map(part => cleanUpAncillaryString(part)).join(', '),
      }
    } else {
      const inNetworkIndex = amounts[0].includes('in-network') ? 0 : 1
      const outOfNetworkIndex = inNetworkIndex === 0 ? 1 : 0

      const inNetworkValue = cleanUpAncillaryString(amounts[inNetworkIndex].replace('in-network', ''))
      const outOfNetworkValue = cleanUpAncillaryString(amounts[outOfNetworkIndex].replace('out-of-network', ''))

      if (inNetworkValue === outOfNetworkValue) {
        return {
          label,
          value: inNetworkValue,
        }
      } else {
        return {
          label,
          inNetworkValue,
          outOfNetworkValue,
        }
      }
    }
  }
}

export const getDentalCoverage = (features: NonMedicalPlanFeature[]): AncillaryLineItem[] => {
  const lineItems: AncillaryLineItem[] = []

  for (const feature of features) {
    if (feature.descriptions.length === 2) {
      if (feature.descriptions[1].includes(ANCILLARY_FEATURE_SEPARATOR)) {
        lineItems.push(ancillaryDescriptionToLineItem(feature.descriptions[1]))
        lineItems.push(ancillaryDescriptionToLineItem(feature.descriptions[0]))
      } else {
        lineItems.push(
          makeDentalLineItem(
            cleanUpAncillaryString(feature.descriptions[1]),
            feature.descriptions[0]
          )
        )
      }
    } else {
      lineItems.push(
        makeDentalLineItem(
          cleanUpAncillaryString(feature.header),
          feature.descriptions[0]
        )
      )
    }
  }

  return lineItems
}

export const getVisionLineItems = (descriptions: string[]): AncillaryLineItem[] => {
  const lineItems: AncillaryLineItem[] = []

  for (const description of descriptions) {
    lineItems.push(ancillaryDescriptionToLineItem(description))
  }

  return lineItems
}
